class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.suggestedSearch = this.querySelector('#suggested-search-results');
    this.redirectsPairs = {
      'return'  : 'https://help.winkscrubs.com/en-US/articles/returns_&_exchanges-61206',
      'returns' : 'https://help.winkscrubs.com/en-US/articles/returns_&_exchanges-61206',
      'shipping': 'https://help.winkscrubs.com/en-US/articles/shipping-61211 ',
    };
    this.exposed          = this.dataset.exposedPs;
    this.enhancedDropdown = null; // resolved in connectedCallback based on A/B flag
    this.moneyFormat      = this.dataset.moneyFormat;
    this.fsUUID           = this.dataset.fsUuid;
    this.fsStoreId        = this.dataset.fsStoreId;
    this.shopDomain       = this.dataset.shopDomain;
    this.cdnCacheKey      = null;
  }

  connectedCallback() {
    this.addDelegatedEventListener();

    // Load Fast Simon config (cdn_cache_key) — no SDK dependency
    this.configReady = this.loadConfig();

    // Init enhanced setup now (if attribute already present) and watch for
    // late injection from Intelligems or manual testing
    this._initEnhanced();
    const attrObserver = new MutationObserver(() => this._initEnhanced());
    attrObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-enhanced-search'] });
  }

  _initEnhanced() {
    const isEnhanced = document.documentElement.dataset.enhancedSearch === 'true';
    const dropdown = this.querySelector('#search-enhanced-dropdown');

    if (dropdown && isEnhanced && !this.enhancedDropdown) {
      this.enhancedDropdown = dropdown;
      this.setupCarousels();
      this._defaultStateCache = this.configReady
        .then(() => this._fetchDefaultState())
        .catch(() => null);
      this.setupEnhancedDropdown();
    } else if (!isEnhanced && this.enhancedDropdown) {
      this.enhancedDropdown = null;
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
    }
  }

  // ─── Fast Simon config ────────────────────────────────────────────────────

  async loadConfig() {
    try {
      const params = new URLSearchParams({ UUID: this.fsUUID, store_id: this.fsStoreId });
      const res    = await fetch(`https://api.fastsimon.com/load?${params}`);
      const data   = await res.json();
      this.cdnCacheKey = data.cdn_cache_key;
    } catch (err) {
      console.warn('[predictive-search] Could not load Fast Simon config', err);
    }
  }

  // ─── Fast Simon raw API ───────────────────────────────────────────────────

  async fetchSuggestionsRaw(query) {
    const params = new URLSearchParams({ q: query, l: this.shopDomain });
    const res    = await fetch(`https://suggest.fastsimon.com/?${params}`);
    const data   = await res.json();
    return data.items || data.suggestions || [];
  }

  _fsParams(extra = {}) {
    return new URLSearchParams({
      UUID         : this.fsUUID,
      store_id     : this.fsStoreId,
      cdn_cache_key: this.cdnCacheKey,
      ...extra,
    });
  }

  async fetchCollectionsRaw(query) {
    if (!this.cdnCacheKey) return [];
    const res  = await fetch(`https://api.fastsimon.com/ac?${this._fsParams({ q: query, with_categories: 'true' })}`);
    const data = await res.json();
    return (data.categories || data.collections || []).map(c => ({
      id   : String(c.id || c.collection_id || ''),
      title: c.l || c.title || c.label || '',
      url  : c.u || c.url || '#',
    }));
  }

  async fetchTrendingRaw() {
    if (!this.cdnCacheKey) return [];
    const res  = await fetch(`https://api.fastsimon.com/ac?${this._fsParams({ q: '' })}`);
    const data = await res.json();
    return data.items || [];
  }

  async fetchProductsDirect(query, count = 10) {
    if (!this.cdnCacheKey) return [];
    const params = new URLSearchParams({
      q                : query,
      UUID             : this.fsUUID,
      store_id         : this.fsStoreId,
      cdn_cache_key    : this.cdnCacheKey,
      products_per_page: count,
      page_num         : 1,
    });
    const res  = await fetch(`https://api.fastsimon.com/full_text_search?${params}`);
    const data = await res.json();
    return data.items || [];
  }

  async _fetchDefaultState() {
    const [suggestResult, trendingResult] = await Promise.allSettled([
      this.fetchSuggestionsRaw(''),
      this.fetchTrendingRaw(),
    ]);
    return {
      popularSearches: suggestResult.status  === 'fulfilled' ? suggestResult.value  : [],
      products       : trendingResult.status === 'fulfilled' ? trendingResult.value : [],
    };
  }

  // ─── Carousel arrows ─────────────────────────────────────────────────────

  setupCarousels() {
    // Handles all [data-carousel-wrapper] elements inside the enhanced dropdown.
    // Called once on connectedCallback; re-called after JS populates a carousel.
    this.enhancedDropdown.querySelectorAll('[data-carousel-wrapper]').forEach(wrapper => {
      this.initCarousel(wrapper);
    });
  }

  initCarousel(wrapper) {
    const track  = wrapper.querySelector('[data-carousel-track]');
    const prev   = wrapper.querySelector('[data-carousel-prev]');
    const next   = wrapper.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;

    const scrollAmount = () => track.clientWidth * 0.75;

    const update = () => {
      const atStart = track.scrollLeft <= 4;
      const atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      prev.classList.toggle('hidden', atStart);
      next.classList.toggle('hidden', atEnd);
    };

    prev.addEventListener('click', () => { track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }); });
    next.addEventListener('click', () => { track.scrollBy({ left:  scrollAmount(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });

    requestAnimationFrame(update);
  }

  // ─── Enhanced dropdown ────────────────────────────────────────────────────

  setupEnhancedDropdown() {
    this.observer = new MutationObserver(() => {
      if (this.classList.contains('open')) {
        this.openEnhanced();
      } else {
        this.closeEnhanced();
      }
    });
    this.observer.observe(this, { attributes: true, attributeFilter: ['class'] });

    // Delegate click tracking — fires SDK events if available, silently skips if not
    this.enhancedDropdown.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-fs-track]');
      if (!link || !window.FastSimonSDK) return;
      const type  = link.dataset.fsTrack;
      const query = this.input.value.trim();

      if (type === 'product') {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.AutocompleteProductClicked,
          data: {
            query,
            productID    : link.dataset.fsProductId,
            productQuery : link.dataset.fsProductQuery,
          },
        });
      } else if (type === 'category') {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.AutocompleteCategoryClicked,
          data: { query, collectionID: link.dataset.fsCollectionId },
        });
      } else if (type === 'popular') {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.AutocompletePopularClicked,
          data: { query, term: link.dataset.fsTerm },
        });
      }
    });
  }

  async openEnhanced() {
    this.enhancedDropdown.classList.remove('hidden');
    this.enhancedDropdown.setAttribute('aria-hidden', 'false');
    if (!this.input.value.trim()) {
      await this.renderDefaultFromCache();
    }
  }

  closeEnhanced() {
    this.enhancedDropdown.classList.add('hidden');
    this.enhancedDropdown.setAttribute('aria-hidden', 'true');
    this.showDefaultPanel();
  }

  showDefaultPanel() {
    this.enhancedDropdown.querySelector('[data-search-left-default]').classList.remove('hidden');
    this.enhancedDropdown.querySelector('[data-search-left-results]').classList.add('hidden');
    this.enhancedDropdown.querySelector('[data-search-right-default]').classList.remove('hidden');
    this.enhancedDropdown.querySelector('[data-search-right-results]').classList.add('hidden');
  }

  showResultsPanel() {
    this.enhancedDropdown.querySelector('[data-search-left-default]').classList.add('hidden');
    this.enhancedDropdown.querySelector('[data-search-left-results]').classList.remove('hidden');
    this.enhancedDropdown.querySelector('[data-search-right-default]').classList.add('hidden');
    this.enhancedDropdown.querySelector('[data-search-right-results]').classList.remove('hidden');
  }

  // ─── Default state ────────────────────────────────────────────────────────

  // Both openEnhanced() and clearing the input call this.
  // Fetches are cached so this renders instantly after the first open.
  async renderDefaultFromCache() {
    this.showDefaultPanel();
    const state = await this._defaultStateCache;
    if (!state) return;

    if (state.popularSearches.length) this.renderPopularSearches(state.popularSearches);
    if (state.products.length)        this.renderTrendingProducts(state.products);
  }

  renderPopularSearches(items) {
    const list = this.enhancedDropdown.querySelector('[data-search-left-default] ul');
    if (!list) return;
    list.innerHTML = items.slice(0, 10).map(item => {
      const term = item.l || item.label || item.text || item;
      return `<li><a href="/pages/search-results-v-next?q=${encodeURIComponent(term)}" data-fs-track="popular" data-fs-term="${term}" class="text-sm text-primaryDark no-underline hover:underline">${term}</a></li>`;
    }).join('');
  }

  renderTrendingProducts(items) {
    const container = this.enhancedDropdown.querySelector('[data-search-right-default] [data-carousel-track]');
    if (!container) return;
    container.innerHTML = items.map(p => this.productCardHTML(p)).join('');
    const wrapper = container.closest('[data-carousel-wrapper]');
    if (wrapper) this.initCarousel(wrapper);
  }

  // ─── Active state ─────────────────────────────────────────────────────────

  async fetchAndRenderResults(searchTerm) {
    await this.configReady;

    const [suggestResult, collectionsResult, productsResult] = await Promise.allSettled([
      this.fetchSuggestionsRaw(searchTerm),
      this.fetchCollectionsRaw(searchTerm),
      this.fetchProductsDirect(searchTerm),
    ]);

    const suggestions = suggestResult.status    === 'fulfilled' ? suggestResult.value    : [];
    const collections = collectionsResult.status === 'fulfilled' ? collectionsResult.value : [];
    const products    = productsResult.status    === 'fulfilled' ? productsResult.value    : [];

    if (!products.length && !suggestions.length) {
      this.showDefaultPanel();
      return;
    }

    this.renderSuggestions(suggestions);
    this.renderCollections(collections);
    this.renderProducts(products, searchTerm);
    this.showResultsPanel();
  }

  renderSuggestions(items) {
    const list = this.enhancedDropdown.querySelector('[data-search-suggestions]');
    list.innerHTML = items.slice(0, 6).map(item => {
      const term = item.l || item.label || item.text || item;
      return `<li><a href="/pages/search-results-v-next?q=${encodeURIComponent(term)}" data-fs-track="popular" data-fs-term="${term}" class="text-sm font-normal text-primaryDark no-underline hover:underline">${term}</a></li>`;
    }).join('');
  }

  renderCollections(items) {
    const wrapper = this.enhancedDropdown.querySelector('[data-search-collections-wrapper]');
    const list    = this.enhancedDropdown.querySelector('[data-search-collections]');
    if (!items.length) { wrapper.classList.add('hidden'); return; }
    wrapper.classList.remove('hidden');
    list.innerHTML = items.slice(0, 4).map(c =>
      `<li><a href="${c.url}" data-fs-track="category" data-fs-collection-id="${c.id}" class="text-sm font-normal text-primaryDark no-underline hover:underline">${c.title}</a></li>`
    ).join('');
  }

  renderProducts(items, searchTerm) {
    const container = this.enhancedDropdown.querySelector('[data-search-products]');
    container.innerHTML = items.map(p => this.productCardHTML(p)).join('');
    this.enhancedDropdown.querySelector('[data-search-view-all]').href = `/pages/search-results-v-next?q=${encodeURIComponent(searchTerm)}`;
    const wrapper = container.closest('[data-carousel-wrapper]');
    if (wrapper) this.initCarousel(wrapper);
  }

  productCardHTML(p) {
    const title  = p.l || p.title || p.name || '';
    const url    = p.u || p.url || p.product_url || '#';
    const imgUrl = p.t || p.image_url || (p.image && p.image.url) || '';
    const price  = this.formatMoney(p.p || p.price || p.price_min_variant);
    const id     = String(p.id || p.product_id || '');
    const pq     = p.s || '';
    return `
      <a href="${url}" data-fs-track="product" data-fs-product-id="${id}" data-fs-product-query="${pq}" class="lg:flex-shrink-0 lg:w-44 no-underline group">
        <div class="aspect-[4/5] overflow-hidden bg-gallery mb-2 rounded-md">
          ${imgUrl ? `<img src="${imgUrl}" alt="${title}" width="176" height="220" loading="lazy" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">` : ''}
        </div>
        <p class="text-xs font-semibold uppercase text-primaryDark leading-snug mb-1 pb-0">${title}</p>
        <p class="text-xs text-primaryDark font-medium">${price}</p>
      </a>`;
  }

  formatMoney(amount) {
    if (amount === undefined || amount === null || amount === '') return '';
    const format  = this.moneyFormat || '${{amount_no_decimals}}';
    const numeric = parseFloat(amount);
    const dollars = numeric > 1000 ? numeric / 100 : numeric;
    return format
      .replace('{{amount_no_decimals}}', Math.round(dollars))
      .replace('{{amount}}', dollars.toFixed(2))
      .replace('{{amount_with_comma_separator}}', dollars.toFixed(2).replace('.', ','));
  }

  // ─── Input handling ───────────────────────────────────────────────────────

  addDelegatedEventListener() {
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.input.addEventListener('keydown', (e) => {
      const searchTerm = this.input.value.trim();
      if (e.key === 'Enter') {
        for (const term in this.redirectsPairs) {
          if (searchTerm === term) {
            e.preventDefault();
            window.location.href = this.redirectsPairs[term];
          }
        }
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm) {
      if (this.enhancedDropdown) {
        this.renderDefaultFromCache();
      } else {
        if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
        if (this.suggestedSearch) this.suggestedSearch.style.display = 'none';
      }
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    if (this.enhancedDropdown) {
      await this.fetchAndRenderResults(searchTerm);
      return;
    }

    // Non-enhanced path
    await this.configReady;
    const products = await this.fetchProductsDirect(searchTerm, 5).catch(() => []);

    if (!products.length) {
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
      if (this.suggestedSearch) {
        this.suggestedSearch.querySelector('[data-search-title]').innerHTML = searchTerm;
        this.suggestedSearch.style.display = 'block';
      }
    } else {
      if (this.suggestedSearch) this.suggestedSearch.style.display = 'none';
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'block';
        this.updateSearchResultsFastSimon(products, searchTerm);
      }
    }
  }

  updateSearchResultsFastSimon(products, searchTerm) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" class="fixed lg:w-[600px] lg:-mt-2 bg-primaryLight rounded-lg p-4 left-4 right-4 ${this.exposed ? 'top-36 exposed-results' : 'top-24'} lg:right-auto lg:left-auto lg:top-auto">
        <ul role="listbox" aria-label="predictive search products" class="flex flex-col">
          ${products.slice(0, 5).map(p => {
            const title = p.l || p.title || p.name || '';
            const url   = p.u || p.url || '#';
            const img   = p.t || p.image_url || '';
            const price = this.formatMoney(p.p || p.price);
            return `<li role="option" class="py-2 border-b border-primaryHalfDark">
              <a href="${url}" class="flex items-center gap-6 no-underline">
                <div class="w-16 shrink-0">${img ? `<img src="${img}" alt="${title}" width="64" loading="lazy">` : ''}</div>
                <div class="flex flex-col gap-1">
                  <span class="text-primaryDark text-sm font-condensed">${title}</span>
                  <span class="text-primaryDark text-sm">${price}</span>
                </div>
              </a>
            </li>`;
          }).join('')}
        </ul>
        <div class="flex pt-8 pb-4 justify-center">
          <a href="/pages/search-results-v-next?q=${encodeURIComponent(searchTerm)}" class="text-sm text-primaryDark font-semibold">View All Results</a>
        </div>
      </div>`;
  }

  updateSearchResults(results, searchTerm) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" class="fixed lg:w-[600px] lg:-mt-2 bg-primaryLight rounded-lg p-4 left-4 right-4 ${this.exposed ? 'top-36 exposed-results' : 'top-24'} lg:right-auto lg:left-auto lg:top-auto">
        <ul role="listbox" aria-label="predictive search products" class="flex flex-col">
          ${results.map((product) => product && `
            <li role="option" class="py-2 border-b border-primaryHalfDark capitalize">
              <intersection-loader
                data-url="${product.url}"
                data-section-id="predictive-search-tile"
              ><span class="block w-full min-h-[69px] bg-gallery rounded-lg"></span></intersection-loader>
            </li>
          `).join('')}
        </ul>
        <div class="flex pt-8 pb-4 justify-center">
          <a href="/pages/search-results-v-next?q=${searchTerm}" class="text-sm text-primaryDark font-semibold">View All Results</a>
        </div>
      </div>`;
  }

  open() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'block';
  }

  close() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
    document.querySelector('[data-action=close-search]')?.click();
  }
}

customElements.define('predictive-search', PredictiveSearch);
